import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import propertyService from "../services/propertyService";

const PropertyContext = createContext();

/**
 * PropertyProvider
 *
 * Purpose: Provides property state and CRUD method signatures.
 * Used by: Property pages, dashboard, and property components.
 */
export function PropertyProvider({ children }) {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1,
    page_size: 20,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tracks the newest in-flight list request. Filter/search changes fire
  // overlapping requests that can resolve out of order, so only the newest one
  // is allowed to write state — otherwise a slow earlier response could clobber
  // a fresh list, or raise an error banner over results that loaded fine.
  const listRequestRef = useRef({ id: 0, controller: null });

  const fetchProperties = useCallback(async (filters = {}) => {
    listRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const requestId = listRequestRef.current.id + 1;
    listRequestRef.current = { id: requestId, controller };
    const isCurrent = () => listRequestRef.current.id === requestId;

    setLoading(true);
    setError(null);
    try {
      const response = await propertyService.list(filters, {
        signal: controller.signal,
      });
      if (!isCurrent()) {
        return response;
      }
      const results = response.results || response.properties || [];
      setProperties(results);
      setPagination({
        count: response.count || results.length,
        next: response.next || null,
        previous: response.previous || null,
        page: Number(filters.page || 1),
        page_size: Number(filters.page_size || 20),
      });
      return response;
    } catch (err) {
      // A superseded request is aborted on purpose — never surface it as a
      // failure, and leave `loading` set for the request that replaced it.
      if (isCurrent()) {
        setError(err.response?.data?.detail || "Failed to load properties");
      }
      throw err;
    } finally {
      if (isCurrent()) {
        setLoading(false);
      }
    }
  }, []);

  const fetchPropertyById = useCallback(async (propertyId) => {
    setLoading(true);
    setError(null);
    try {
      const property = await propertyService.get(propertyId);
      setSelectedProperty(property);
      return property;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load property");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createProperty = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const created = await propertyService.create(data);
      setProperties((current) => [created, ...current]);
      setSelectedProperty(created);
      return created;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create property");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProperty = useCallback(async (propertyId, data) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await propertyService.update(propertyId, data);
      setProperties((current) =>
        current.map((item) =>
          item.property_id === propertyId ? updated : item,
        ),
      );
      setSelectedProperty(updated);
      return updated;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update property");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteProperty = useCallback(
    async (propertyId) => {
      setLoading(true);
      setError(null);
      try {
        await propertyService.delete(propertyId);
        setProperties((current) =>
          current.filter((item) => item.property_id !== propertyId),
        );
        if (selectedProperty?.property_id === propertyId) {
          setSelectedProperty(null);
        }
        return true;
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to delete property");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [selectedProperty?.property_id],
  );

  const value = useMemo(
    () => ({
      properties,
      selectedProperty,
      pagination,
      loading,
      error,
      fetchProperties,
      fetchPropertyById,
      createProperty,
      updateProperty,
      deleteProperty,
    }),
    [
      properties,
      selectedProperty,
      pagination,
      loading,
      error,
      fetchProperties,
      fetchPropertyById,
      createProperty,
      updateProperty,
      deleteProperty,
    ],
  );

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
}

PropertyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const usePropertyContext = () => {
  const context = useContext(PropertyContext);

  if (!context) {
    throw new Error("usePropertyContext must be used within PropertyProvider");
  }

  return context;
};

export default PropertyContext;
